import { getErrorMessage } from 'shared/utils/errorHandling';
import { createChannelBus, describeChannelMessage } from './channelBus';
import { ChannelClientEnvironment, createChannelClient } from './channelClient';
import { createChannelServer } from './channelServer';
import { ChannelDiagnosticEvent } from './errors';

export interface ChannelContextConfiguration {
  /** How the page reaches the service worker (wake-up, diagnostics). */
  clientEnvironment?: Omit<ChannelClientEnvironment, 'reportEvent'>;
  /** Failures only this context sees, e.g. a failing subscriber: logged as errors. */
  reportError?: (message: string, details: Record<string, unknown>) => void;
  /** Tuning signals and failures already answered to the requester: logged as info. */
  reportEvent?: (event: ChannelDiagnosticEvent, details: Record<string, unknown>) => void;
  /** Resolves once this context may answer requests (the worker: its services started). */
  waitUntilServingReady?: () => Promise<unknown>;
}

let configuration: Required<ChannelContextConfiguration> = {
  clientEnvironment: {
    prepareToSend: async () => () => ({}),
    markResponderStale: () => undefined,
    reportTimeout: () => undefined,
  },
  reportError: () => undefined,
  reportEvent: () => undefined,
  waitUntilServingReady: () => Promise.resolve(),
};

/**
 * Sets what differs between the page and the service worker. Called once by
 * each composition root (`src/index.tsx`, `src/sw.ts`) before any request.
 */
export function configureChannelContext(next: ChannelContextConfiguration): void {
  configuration = { ...configuration, ...next };
}

/** One per context (page or service worker): every channel of the app goes through it. */
export const channelBus = createChannelBus({
  onHandlerError: (contractKey, error, message) =>
    configuration.reportError(`Channel message handler failed on ${contractKey}`, {
      error,
      messageType: describeChannelMessage(message),
    }),
  // A message that cannot be cloned is a programming error nobody else would see.
  onPostError: (contractKey, error, message) =>
    configuration.reportError(`Channel message could not be sent on ${contractKey}`, {
      error,
      messageType: describeChannelMessage(message),
    }),
});

export const channelClient = createChannelClient({
  bus: channelBus,
  environment: {
    prepareToSend: () => configuration.clientEnvironment.prepareToSend(),
    markResponderStale: () => configuration.clientEnvironment.markResponderStale(),
    reportTimeout: (error) => configuration.clientEnvironment.reportTimeout(error),
    reportEvent: (event, details) => configuration.reportEvent(event, details),
  },
});

export const channelServer = createChannelServer({
  bus: channelBus,
  waitUntilServingReady: () => configuration.waitUntilServingReady(),
  onHandlerError: (method, error, requestId) =>
    configuration.reportEvent(ChannelDiagnosticEvent.requestHandlerFailed, {
      method,
      requestId,
      error: getErrorMessage(error),
    }),
});
